import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import SafetyForm from '../components/SafetyForm';
import { 
    ShoppingCart, 
    Trash2, 
    CreditCard, 
    ArrowRight, 
    ArrowLeft,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Ticket,
    X,
    Clock,
    AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import SEOHead, { pageSEO } from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HoldCountdown({ expiresAt }) {
    const [timeLeft, setTimeLeft] = useState('');
    const [isUrgent, setIsUrgent] = useState(false);

    useEffect(function() {
        function update() {
            if (!expiresAt) { setTimeLeft(''); return; }
            var now = new Date();
            var expiry = new Date(expiresAt);
            var diff = Math.max(0, Math.floor((expiry - now) / 1000));
            var mins = Math.floor(diff / 60);
            var secs = diff % 60;
            setTimeLeft(mins + ':' + (secs < 10 ? '0' : '') + secs);
            setIsUrgent(diff < 120);
        }
        update();
        var interval = setInterval(update, 1000);
        return function() { clearInterval(interval); };
    }, [expiresAt]);

    if (!expiresAt || !timeLeft) return null;

    return (
        <div className={'flex items-center gap-1.5 text-xs font-medium ' + (isUrgent ? 'text-red-600' : 'text-amber-600')} data-testid="hold-countdown">
            <Clock className="w-3.5 h-3.5" />
            <span>Place held for {timeLeft}</span>
        </div>
    );
}

export default function CartView() {
    const { cart, cartId, loading, removeFromCart, checkout, appliedDiscount, applyDiscount, removeDiscount: contextRemoveDiscount, finalTotal, holdExpired, dismissHoldExpired } = useCart();
    const [step, setStep] = useState(1); // 1 = Cart, 2 = Safety Form
    const [isFormValid, setIsFormValid] = useState(false);
    const [safetyFormData, setSafetyFormData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Discount code state
    const [discountCode, setDiscountCode] = useState('');
    const [applyingDiscount, setApplyingDiscount] = useState(false);

    const applyDiscountCode = async () => {
        if (!discountCode.trim()) {
            toast.error('Please enter a discount code or voucher');
            return;
        }
        
        setApplyingDiscount(true);
        try {
            // Try discount code first
            try {
                const result = await applyDiscount(discountCode);
                if (result.success) {
                    toast.success(`Discount applied: ${result.data.discount_type === 'percentage' ? result.data.discount_value + '% off' : '£' + result.data.discount_amount + ' off'}`);
                    return;
                }
            } catch (discountError) {
                // If not a discount code, try gift voucher
                if (discountCode.startsWith('GV-')) {
                    const voucherResponse = await axios.post(`${API}/gift-vouchers/redeem`, {
                        code: discountCode,
                        cart_total: cart.total
                    });
                    // Store voucher as discount in context
                    await applyDiscount(discountCode);
                    toast.success(`Gift voucher applied: £${voucherResponse.data.discount_amount} off`);
                    return;
                }
                throw discountError;
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid code');
            contextRemoveDiscount();
        } finally {
            setApplyingDiscount(false);
        }
    };

    const removeDiscountHandler = () => {
        contextRemoveDiscount();
        setDiscountCode('');
        toast.success('Discount removed');
    };

    const handleCheckout = async () => {
        if (!isFormValid) {
            toast.error('Please complete all required fields in the safety form');
            return;
        }

        setIsSubmitting(true);

        try {
            // First, save the safety form data
            await axios.post(`${API}/safety-form`, {
                cart_id: cartId,
                ...safetyFormData
            });

            // Then proceed to checkout - discount code is already in context
            const result = await checkout();
            
            // If discount/voucher was applied successfully, record its usage
            if (result.success && appliedDiscount) {
                try {
                    // Record discount code usage
                    await axios.post(`${API}/discount/use?code=${appliedDiscount.code}`);
                } catch (e) {
                }
            }
            
            if (result.success) {
                // Clear discount after successful checkout
                contextRemoveDiscount();
                window.location.href = result.url;
            } else {
                toast.error(result.error || 'Checkout failed');
            }
        } catch (error) {
            toast.error('Failed to process checkout. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemove = async (id) => {
        await removeFromCart(id);
        toast.success('Item removed');
    };

    const items = cart.items;
    const total = cart.total;

    return (
        <div className="min-h-screen bg-secondary/20" data-testid="cart-page">
            <SEOHead 
                title={pageSEO.cart.title}
                description={pageSEO.cart.description}
                canonical={pageSEO.cart.canonical}
                noindex={true}
            />
            {/* Header */}
            <section className="py-8 bg-white border-b">
                <div className="container-custom">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground font-serif">
                                {step === 1 ? 'Your Cart' : 'Safety Information'}
                            </h1>
                            <p className="text-muted-foreground">
                                {step === 1 ? `${items.length} item${items.length !== 1 ? 's' : ''} in your cart` : 'Complete this form before payment'}
                            </p>
                        </div>
                        {/* Progress Steps */}
                        <div className="hidden md:flex items-center gap-4">
                            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    step >= 1 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
                                }`}>
                                    {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
                                </div>
                                <span className="font-medium">Cart</span>
                            </div>
                            <div className="w-12 h-0.5 bg-border" />
                            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    step >= 2 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
                                }`}>
                                    2
                                </div>
                                <span className="font-medium">Safety Form</span>
                            </div>
                            <div className="w-12 h-0.5 bg-border" />
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-secondary">
                                    3
                                </div>
                                <span className="font-medium">Payment</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="container-custom">
                    {/* Hold Expired Banner */}
                    {holdExpired && items.length === 0 && (
                        <Card className="max-w-lg mx-auto text-center border-amber-200 bg-amber-50" data-testid="hold-expired-banner">
                            <CardContent className="py-12">
                                <AlertTriangle className="w-14 h-14 mx-auto mb-4 text-amber-500" />
                                <h2 className="text-xl font-bold mb-2 text-foreground">Your reserved place has expired</h2>
                                <p className="text-muted-foreground mb-6">
                                    Places are held for 10 minutes. The session is now available for other customers.
                                    Please add it to your basket again.
                                </p>
                                <div className="flex flex-col gap-3 items-center">
                                    <Link to="/mantrailing#booking-section">
                                        <Button className="btn-primary" onClick={dismissHoldExpired}>
                                            Book Again
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                    {items.length === 0 && !holdExpired ? (
                        <Card className="max-w-lg mx-auto text-center" data-testid="empty-cart">
                            <CardContent className="py-16">
                                <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                                <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
                                <p className="text-muted-foreground mb-6">
                                    Add a mantrailing session to get started
                                </p>
                                <Link to="/mantrailing">
                                    <Button className="btn-primary">
                                        Browse Sessions
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            {/* Step 1: Cart Items */}
                            {step === 1 && (
                                <div className="grid lg:grid-cols-3 gap-8">
                                    <div className="lg:col-span-2 space-y-4">
                                        {items.map((item) => (
                                            <Card key={item.id} data-testid={`cart-item-${item.id}`}>
                                                <CardContent className="p-6">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                            <h3 className="font-bold text-lg text-foreground">{item.session_name}</h3>
                                                            <p className="text-muted-foreground mt-1">
                                                                {item.date} at {item.time}
                                                            </p>
                                                            <div className="flex items-center gap-3 mt-2">
                                                                <Badge className="bg-primary/10 text-primary">
                                                                    {item.status || 'Pending'}
                                                                </Badge>
                                                                {item.hold_expires_at && (
                                                                    <HoldCountdown expiresAt={item.hold_expires_at} />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-2xl font-bold text-primary">
                                                                £{item.price.toFixed(2)}
                                                            </span>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm" 
                                                                className="mt-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                onClick={() => handleRemove(item.id)}
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-1" />
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                    
                                    <div>
                                        <Card className="sticky top-24">
                                            <CardHeader>
                                                <CardTitle>Order Summary</CardTitle>
                                            </CardHeader>
                                            <CardContent className="space-y-4">
                                                <div className="space-y-2 pb-4 border-b">
                                                    {items.map((item) => (
                                                        <div key={`summary-${item.id}`} className="flex justify-between text-sm">
                                                            <span className="text-muted-foreground">{item.session_name}</span>
                                                            <span>£{item.price.toFixed(2)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                
                                                {/* Discount Code Section */}
                                                <div className="pb-4 border-b">
                                                    {!appliedDiscount ? (
                                                        <div className="space-y-2">
                                                            <label className="text-sm font-medium flex items-center gap-2">
                                                                <Ticket className="w-4 h-4" />
                                                                Discount Code
                                                            </label>
                                                            <div className="flex gap-2">
                                                                <Input
                                                                    value={discountCode}
                                                                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                                                                    placeholder="Enter code"
                                                                    className="flex-1"
                                                                    data-testid="discount-code-input"
                                                                />
                                                                <Button 
                                                                    variant="outline" 
                                                                    onClick={applyDiscountCode}
                                                                    disabled={applyingDiscount}
                                                                    data-testid="apply-discount-btn"
                                                                >
                                                                    {applyingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                            <div className="flex justify-between items-center">
                                                                <div>
                                                                    <p className="font-medium text-green-800 flex items-center gap-2">
                                                                        <CheckCircle2 className="w-4 h-4" />
                                                                        {appliedDiscount.code}
                                                                    </p>
                                                                    <p className="text-sm text-green-600">
                                                                        {appliedDiscount.discount_type === 'percentage' 
                                                                            ? `${appliedDiscount.discount_value}% off`
                                                                            : `£${appliedDiscount.discount_value} off`}
                                                                    </p>
                                                                </div>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={removeDiscountHandler}
                                                                    className="text-red-500 hover:text-red-600"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Totals */}
                                                {appliedDiscount ? (
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-muted-foreground">Subtotal</span>
                                                            <span>£{total.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm text-green-600">
                                                            <span>Discount</span>
                                                            <span>-£{appliedDiscount.discount_amount.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex justify-between font-bold text-lg pt-2 border-t">
                                                            <span>Total</span>
                                                            <span className="text-primary">£{appliedDiscount.new_total.toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-between font-bold text-lg">
                                                        <span>Total</span>
                                                        <span className="text-primary">£{total.toFixed(2)}</span>
                                                    </div>
                                                )}

                                                <Button 
                                                    className="w-full btn-primary"
                                                    onClick={() => setStep(2)}
                                                    data-testid="proceed-to-safety-form"
                                                >
                                                    Continue to Safety Form
                                                    <ArrowRight className="w-4 h-4 ml-2" />
                                                </Button>
                                                <p className="text-xs text-center text-muted-foreground">
                                                    You'll need to complete a safety form before payment
                                                </p>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Safety Form */}
                            {step === 2 && (
                                <div className="max-w-4xl mx-auto">
                                    {/* Back Button */}
                                    <Button 
                                        variant="outline" 
                                        onClick={() => setStep(1)}
                                        className="mb-6"
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Back to Cart
                                    </Button>

                                    {/* Safety Form */}
                                    <SafetyForm 
                                        onFormValid={setIsFormValid}
                                        onFormData={setSafetyFormData}
                                    />

                                    {/* Order Summary & Checkout Button */}
                                    <Card className="mt-8 sticky bottom-4 shadow-lg border-2 border-primary/20">
                                        <CardContent className="p-6">
                                            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                                <div className="flex items-center gap-4">
                                                    <div>
                                                        <p className="text-sm text-muted-foreground">Order Total</p>
                                                        {appliedDiscount ? (
                                                            <div>
                                                                <p className="text-sm text-muted-foreground line-through">£{total.toFixed(2)}</p>
                                                                <p className="text-2xl font-bold text-primary">£{finalTotal.toFixed(2)}</p>
                                                                <p className="text-xs text-green-600">Discount: {appliedDiscount.code}</p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-2xl font-bold text-primary">£{total.toFixed(2)}</p>
                                                        )}
                                                    </div>
                                                    <div className="hidden md:block h-12 w-px bg-border" />
                                                    <div className="flex items-center gap-2">
                                                        {isFormValid ? (
                                                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                                                        ) : (
                                                            <AlertCircle className="w-5 h-5 text-amber-500" />
                                                        )}
                                                        <span className={`text-sm ${isFormValid ? 'text-green-600' : 'text-amber-600'}`}>
                                                            {isFormValid ? 'Form complete' : 'Please complete all required fields'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <Button 
                                                    className="w-full md:w-auto btn-primary min-w-[200px]"
                                                    onClick={handleCheckout}
                                                    disabled={!isFormValid || isSubmitting}
                                                    data-testid="proceed-to-payment"
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Processing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CreditCard className="w-4 h-4 mr-2" />
                                                            Proceed to Payment
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
